<template>
    <section v-if="items.length" class="w-full border-t border-theme-border pt-3">
        <div class="mb-1 flex items-center justify-between">
            <h3 class="block text-xs font-semibold uppercase tracking-wide text-theme-toolbar-icon">
                {{ localizations[lang].print.historyTitle }}
                <span class="ml-1">({{ items.length }})</span>
            </h3>
            <BaseButton
                v-if="isCollapsible"
                :aria-label="toggleLabel"
                :aria-expanded="String(isExpanded)"
                @click="toggleExpanded"
            >
                <svg-icon
                    :icon="isExpanded ? 'baseline-expand_less-24px' : 'baseline-expand_more-24px'"
                    class="text-theme-icon"
                    width="20px"
                    height="20px"
                />
            </BaseButton>
        </div>
        <ul>
            <li
                v-for="(item, index) in visibleItems"
                :key="`${item.pageFrom}-${item.pageTo}-${index}`"
                class="flex items-center justify-between py-1 text-sm text-theme-text"
            >
                <span>{{ rangeLabel(item) }}</span>
                <BaseButton variant="ghost" :aria-label="reprintLabel(item)" @click="emitReprint(item)">
                    {{ localizations[lang].print.reprint }}
                </BaseButton>
            </li>
        </ul>
    </section>
</template>

<script>
import { mapState } from 'vuex';
import BaseButton from '../Components/buttons/BaseButton.vue';

const HISTORY_COLLAPSE_THRESHOLD = 3;

export default {
    name: 'PrintHistoryList',

    components: {
        BaseButton,
    },

    props: {
        items: {
            type: Array,
            default: () => [],
        },
    },

    emits: ['reprint'],

    data() {
        return {
            isExpanded: false,
        };
    },

    computed: {
        ...mapState(['localizations', 'lang']),
        /**
         * Whether the history exceeds the threshold and should be collapsed behind a toggle.
         * @returns {boolean}
         */
        isCollapsible() {
            return this.items.length > HISTORY_COLLAPSE_THRESHOLD;
        },
        /**
         * History entries currently rendered: all of them when expanded or below the
         * threshold, otherwise none (the whole list stays collapsed behind the toggle).
         * @returns {Array<{pageFrom: number, pageTo: number}>}
         */
        visibleItems() {
            if (this.isExpanded || !this.isCollapsible) {
                return this.items;
            }
            return [];
        },
        /**
         * Localized accessible label for the expand/collapse toggle.
         * @returns {string}
         */
        toggleLabel() {
            return this.localizations[this.lang].print[this.isExpanded ? 'historyCollapse' : 'historyExpand'];
        },
    },

    methods: {
        /**
         * Builds the localized "Pages {from} - {to}" label for a history entry.
         * @param {{ pageFrom: number, pageTo: number }} item
         * @returns {string}
         */
        rangeLabel(item) {
            return this.localizations[this.lang].print.historyRange
                .replace('{from}', item.pageFrom)
                .replace('{to}', item.pageTo);
        },
        /**
         * Builds the localized accessible label for the reprint button so screen readers
         * announce each row's page range instead of a repeated generic "Print".
         * @param {{ pageFrom: number, pageTo: number }} item
         * @returns {string}
         */
        reprintLabel(item) {
            return this.localizations[this.lang].print.reprintRange
                .replace('{from}', item.pageFrom)
                .replace('{to}', item.pageTo);
        },
        emitReprint(item) {
            this.$emit('reprint', item);
        },
        toggleExpanded() {
            this.isExpanded = !this.isExpanded;
        },
    },
};
</script>
